import {
  Children,
  isValidElement,
  ReactElement,
  ReactNode,
  useContext,
} from 'react';
import styled from 'styled-components';
import type { ExtraProps } from 'react-markdown';
import DownloadRoundedIcon from '@mui/icons-material/DownloadRounded';
import { Tooltip } from '@mui/material';
import { Column } from '../../Table/types';
import { Flex } from '../../Flex';
import IconButton from '../../IconButton';
import { TrackingContext } from '../../../providers/tracking';
import { ChatTable } from '../toolRenderers/tableToolRenderer';
import { downloadTableAsCSV } from '../utils';

type CellRow = Record<string, ReactNode>;

// The shared Table cell forces `white-space: nowrap`; markdown cells can hold
// long text, so we re-enable wrapping and break long tokens within the cell.
const CellContent = styled.div`
  white-space: normal;
  overflow-wrap: anywhere;
`;

const getChildElements = (
  children: ReactNode,
  tagName: string,
): ReactElement[] =>
  Children.toArray(children).filter(
    (child): child is ReactElement =>
      isValidElement(child) && child.type === tagName,
  );

const getNodeText = (node: ReactNode): string => {
  if (node === null || node === undefined || typeof node === 'boolean') {
    return '';
  }
  if (typeof node === 'string') return node;
  if (typeof node === 'number') return String(node);
  if (Array.isArray(node)) return node.map(getNodeText).join('');
  if (isValidElement(node)) return getNodeText(node.props.children);
  return '';
};

type Props = JSX.IntrinsicElements['table'] & ExtraProps;

// Native GFM tables are parsed by react-markdown into thead/tbody React trees.
// We extract the header labels and the already-rendered rich cell nodes (which
// include inline code/links/bold) and feed them into the shared Table component
// so markdown tables get the same behavior as the table_display tool renderer.
const MarkdownTable = ({ children }: Props) => {
  const thead = getChildElements(children, 'thead')[0];
  const tbody = getChildElements(children, 'tbody')[0];

  const headerRow = getChildElements(thead?.props.children, 'tr')[0];
  const headerCells = getChildElements(headerRow?.props.children, 'th');

  const columns: Column<CellRow>[] = headerCells.map((cell, index) => {
    const id = `col_${index}`;
    return {
      id,
      header: getNodeText(cell.props.children),
      filterable: false,
      renderer: (row: CellRow) => <CellContent>{row[id]}</CellContent>,
    };
  });

  const rows: CellRow[] = getChildElements(tbody?.props.children, 'tr')
    .map((tableRow) => {
      const cells = getChildElements(tableRow.props.children, 'td');
      const row: CellRow = {};
      columns.forEach((column, index) => {
        row[column.id] = cells[index]?.props.children ?? null;
      });
      return row;
    })
    .filter((row) =>
      columns.some((column) => getNodeText(row[column.id]).trim() !== ''),
    );

  const tracking = useContext(TrackingContext);

  if (columns.length === 0) {
    return <table>{children}</table>;
  }

  const handleDownload = () => {
    const csvColumns = columns.map((column) => column.header);
    const csvRows = rows.map((row) => {
      const csvRow: Record<string, string> = {};
      columns.forEach((column) => {
        csvRow[column.header] = getNodeText(row[column.id]);
      });
      return csvRow;
    });
    downloadTableAsCSV(csvRows, csvColumns);
    tracking?.sendTrackingEvent({
      eventName: tracking.analytics.events.AI_CHAT_TABLE_CSV_DOWNLOAD,
    });
  };

  return (
    <>
      <Flex sx={{ justifyContent: 'flex-end', mb: '4px' }}>
        <Tooltip title="Export CSV">
          <IconButton onClick={handleDownload}>
            <DownloadRoundedIcon sx={{ fontSize: '18px' }} />
          </IconButton>
        </Tooltip>
      </Flex>
      <ChatTable rows={rows} columns={columns} />
    </>
  );
};

export default MarkdownTable;
